import { useState } from "react";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-[#0d0d0d] border border-zinc-700 focus:border-[#E10600] outline-none px-4 py-3 text-white placeholder:text-zinc-600 transition-colors duration-300 text-sm";

export default function TicketsAdmin({ tickets, reload }) {
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const sendReply = async (id) => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/tickets/${id}/reply`, { message: reply });
      toast.success("Reply sent to fighter");
      setReply("");
      reload();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/tickets/${id}/status`, { status });
      toast.success(`Ticket ${status}`);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="admin-tickets-section">
      <h2 className="font-display text-3xl uppercase text-white mb-6 flex items-center gap-3">
        <MessageSquare className="h-6 w-6 text-[#E10600]" /> Support Tickets
      </h2>
      {tickets.length === 0 ? (
        <p className="font-mono2 text-xs tracking-[0.3em] text-zinc-500 uppercase border border-zinc-800 p-8 text-center" data-testid="admin-tickets-empty">
          No tickets yet
        </p>
      ) : (
        <div className="space-y-3" data-testid="admin-tickets-list">
          {tickets.map((t) => (
            <div key={t.id} className="border border-zinc-800 bg-[#111111]" data-testid={`admin-ticket-${t.id}`}>
              <button
                onClick={() => setOpenId(openId === t.id ? null : t.id)}
                className="w-full flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5 px-5 py-4 text-left hover:bg-[#161616] transition-colors"
                data-testid={`admin-ticket-toggle-${t.id}`}
              >
                <span className="font-cond font-bold text-lg uppercase text-white truncate flex-1">{t.subject}</span>
                <span className="font-mono2 text-[10px] tracking-[0.15em] text-zinc-500">{t.fighter_name} · {t.email}</span>
                <span className={`font-mono2 text-[9px] tracking-[0.25em] uppercase border px-2.5 py-1 w-max ${t.status === "open" ? "text-yellow-500 border-yellow-800/60" : "text-zinc-500 border-zinc-700"}`} data-testid={`admin-ticket-status-${t.id}`}>
                  {t.status}
                </span>
              </button>
              {openId === t.id && (
                <div className="border-t border-zinc-800 p-5 space-y-3" data-testid={`admin-ticket-thread-${t.id}`}>
                  {t.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${m.sender === "admin" ? "bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-zinc-100" : "bg-[#1a1a1a] border border-zinc-800 text-zinc-200"}`}>
                        <p className="font-mono2 text-[8px] tracking-[0.25em] uppercase mb-1 opacity-60">
                          {m.sender === "admin" ? "You (MPB Team)" : t.fighter_name} · {new Date(m.at).toLocaleString()}
                        </p>
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <input className={inputCls} placeholder="Reply to fighter..." value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`admin-ticket-reply-input-${t.id}`} />
                    <div className="flex gap-2">
                      <button onClick={() => sendReply(t.id)} disabled={busy} data-testid={`admin-ticket-reply-button-${t.id}`} className="font-cond font-semibold tracking-[0.2em] bg-[#E10600] hover:bg-[#C00500] disabled:opacity-50 text-white px-6 py-3 transition-colors">
                        REPLY
                      </button>
                      <button
                        onClick={() => setStatus(t.id, t.status === "open" ? "closed" : "open")}
                        data-testid={`admin-ticket-close-${t.id}`}
                        className="font-cond font-semibold tracking-[0.2em] border border-zinc-700 hover:border-zinc-400 text-zinc-300 px-6 py-3 transition-colors"
                      >
                        {t.status === "open" ? "CLOSE" : "REOPEN"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
